import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTokens } from '../../context/TokenContext';
import { createCheckoutSession, createCustomerPortalSession } from '../../services/tokenService';
import confetti from 'canvas-confetti';
import {
  X,
  Sparkles,
  Crown,
  Check,
  Zap,
  Flame,
  ExternalLink,
  Loader2,
  ShieldCheck,
  Film
} from 'lucide-react';

export const TokenPurchaseModal: React.FC = () => {
  const {
    balance,
    isPro,
    tier,
    canClaimStreak,
    streakCountdownMs,
    isPurchaseModalOpen,
    defaultModalTab,
    closePurchaseModal,
    claimStreak
  } = useTokens();

  const [activeTab, setActiveTab] = useState<'packs' | 'pro'>(defaultModalTab);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [streakClaiming, setStreakClaiming] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setActiveTab(defaultModalTab);
  }, [defaultModalTab]);

  useEffect(() => {
    if (!isPurchaseModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closePurchaseModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPurchaseModalOpen, closePurchaseModal]);

  if (!isPurchaseModalOpen) return null;

  const handleClaimStreak = async () => {
    try {
      setStreakClaiming(true);
      setErrorMessage(null);
      const ok = await claimStreak();
      if (ok) {
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.6 }
        });
      }
    } catch (e: any) {
      setErrorMessage(e.message || 'Failed to claim daily streak.');
    } finally {
      setStreakClaiming(false);
    }
  };

  const handleBuyPack = async (packId: string) => {
    try {
      setIsProcessing(packId);
      setErrorMessage(null);
      const res = await createCheckoutSession({ packId });
      if (res.success && res.url) {
        window.location.href = res.url;
      } else {
        setErrorMessage(res.error || 'Failed to start Stripe checkout session.');
      }
    } catch (e: any) {
      setErrorMessage(e.message || 'Payment initiation failed.');
    } finally {
      setIsProcessing(null);
    }
  };

  const handleSubscribePro = async () => {
    try {
      setIsProcessing('creator_pro');
      setErrorMessage(null);
      const res = await createCheckoutSession({ tier: 'creator_pro' });
      if (res.success && res.url) {
        window.location.href = res.url;
      } else {
        setErrorMessage(res.error || 'Failed to start subscription checkout.');
      }
    } catch (e: any) {
      setErrorMessage(e.message || 'Subscription initiation failed.');
    } finally {
      setIsProcessing(null);
    }
  };

  const handleOpenPortal = async () => {
    try {
      setIsProcessing('portal');
      setErrorMessage(null);
      const res = await createCustomerPortalSession();
      if (res.success && res.url) {
        window.location.href = res.url;
      } else {
        setErrorMessage(res.error || 'Failed to open customer portal.');
      }
    } catch (e: any) {
      setErrorMessage(e.message || 'Portal redirect failed.');
    } finally {
      setIsProcessing(null);
    }
  };

  const formatCountdown = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        background: 'rgba(0, 0, 0, 0.78)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        overflowY: 'auto'
      }}
      onClick={closePurchaseModal}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '580px',
          maxHeight: 'calc(100vh - 3rem)',
          margin: 'auto',
          overflowY: 'auto',
          background: 'linear-gradient(180deg, #181824 0%, #0f0f17 100%)',
          border: '1px solid rgba(168, 85, 247, 0.35)',
          borderRadius: '18px',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8), 0 0 35px rgba(168, 85, 247, 0.18)',
          color: '#ffffff',
          padding: '1.5rem',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={closePurchaseModal}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'rgba(255, 255, 255, 0.08)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#a1a1aa',
            cursor: 'pointer'
          }}
        >
          <X size={18} />
        </button>

        {/* Header with Balance */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.25rem' }}>
          <div
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '12px',
              background: isPro
                ? 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)'
                : 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isPro ? '0 0 16px rgba(234, 179, 8, 0.4)' : '0 0 16px rgba(168, 85, 247, 0.4)'
            }}
          >
            {isPro ? <Crown size={26} color="#000" /> : <Sparkles size={24} color="#fff" />}
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, letterSpacing: '-0.01em' }}>
              SoulSync Token Store
            </h2>
            <p style={{ margin: 0, fontSize: '0.84rem', color: '#a1a1aa' }}>
              Current Balance:{' '}
              <strong style={{ color: '#f3e8ff', fontSize: '0.95rem' }}>{balance} Tokens</strong>
              {isPro && (
                <span
                  style={{
                    marginLeft: '8px',
                    fontSize: '0.7rem',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: 'rgba(234, 179, 8, 0.2)',
                    border: '1px solid rgba(234, 179, 8, 0.5)',
                    color: '#facc15',
                    fontWeight: 700
                  }}
                >
                  CREATOR PRO
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Daily Streak Reward Banner */}
        <div
          style={{
            background: canClaimStreak
              ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)'
              : 'rgba(255, 255, 255, 0.04)',
            border: canClaimStreak
              ? '1px solid rgba(34, 197, 94, 0.4)'
              : '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '12px',
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1.25rem'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Flame size={20} color={canClaimStreak ? '#4ade80' : '#71717a'} />
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: canClaimStreak ? '#4ade80' : '#e4e4e7' }}>
                Daily Login Streak
              </div>
              <div style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>
                {canClaimStreak
                  ? '+1 Free Token ready to claim!'
                  : `Next claim in ${formatCountdown(streakCountdownMs)}`}
              </div>
            </div>
          </div>
          {canClaimStreak ? (
            <button
              type="button"
              onClick={handleClaimStreak}
              disabled={streakClaiming}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                border: 'none',
                color: '#ffffff',
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 0 12px rgba(34, 197, 94, 0.3)'
              }}
            >
              {streakClaiming ? <Loader2 size={14} className="spin" /> : <Zap size={14} />}
              Claim +1 Token
            </button>
          ) : (
            <span style={{ fontSize: '0.78rem', color: '#71717a', fontWeight: 600 }}>Claimed Today</span>
          )}
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              color: '#f87171',
              fontSize: '0.82rem',
              marginBottom: '1rem'
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* Store Tabs */}
        <div
          style={{
            display: 'flex',
            borderRadius: '10px',
            background: 'rgba(0, 0, 0, 0.35)',
            padding: '4px',
            marginBottom: '1.25rem'
          }}
        >
          <button
            type="button"
            onClick={() => setActiveTab('packs')}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '8px',
              background: activeTab === 'packs' ? 'rgba(168, 85, 247, 0.25)' : 'transparent',
              border: activeTab === 'packs' ? '1px solid rgba(168, 85, 247, 0.45)' : 'none',
              color: activeTab === 'packs' ? '#ffffff' : '#a1a1aa',
              fontSize: '0.85rem',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s ease'
            }}
          >
            <Sparkles size={15} color="#c084fc" />
            Token Micro-Packs
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('pro')}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '8px',
              background: activeTab === 'pro' ? 'rgba(234, 179, 8, 0.25)' : 'transparent',
              border: activeTab === 'pro' ? '1px solid rgba(234, 179, 8, 0.45)' : 'none',
              color: activeTab === 'pro' ? '#ffffff' : '#a1a1aa',
              fontSize: '0.85rem',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s ease'
            }}
          >
            <Crown size={15} color="#facc15" />
            Creator Pro ($9.99/mo)
          </button>
        </div>

        {/* Tab 1: Token Packs */}
        {activeTab === 'packs' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '1.25rem' }}>
            {/* Starter Pack */}
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'all 0.15s ease'
              }}
            >
              <div>
                <div style={{ fontSize: '0.8rem', color: '#a1a1aa', fontWeight: 600 }}>Starter</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ffffff', margin: '4px 0' }}>
                  5 <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#d8b4fe' }}>Tokens</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginBottom: '12px' }}>
                  $0.20 / token • Zero friction
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleBuyPack('starter')}
                disabled={Boolean(isProcessing)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '8px',
                  background: 'rgba(168, 85, 247, 0.25)',
                  border: '1px solid rgba(168, 85, 247, 0.45)',
                  color: '#ffffff',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {isProcessing === 'starter' ? <Loader2 size={14} className="spin" /> : '$1.00 USD'}
              </button>
            </div>

            {/* Creator Pack */}
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.12) 0%, rgba(99, 102, 241, 0.08) 100%)',
                border: '1px solid rgba(168, 85, 247, 0.4)',
                borderRadius: '12px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative'
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '-9px',
                  right: '12px',
                  background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                  padding: '1px 8px',
                  borderRadius: '9999px',
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  color: '#ffffff'
                }}
              >
                MOST POPULAR (+20%)
              </div>
              <div>
                <div style={{ fontSize: '0.8rem', color: '#c084fc', fontWeight: 600 }}>Creator Pack</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ffffff', margin: '4px 0' }}>
                  30 <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#d8b4fe' }}>Tokens</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginBottom: '12px' }}>
                  $0.166 / token • 6 free bonus
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleBuyPack('creator')}
                disabled={Boolean(isProcessing)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 0 12px rgba(168, 85, 247, 0.35)'
                }}
              >
                {isProcessing === 'creator' ? <Loader2 size={14} className="spin" /> : '$4.99 USD'}
              </button>
            </div>

            {/* Studio Pack */}
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <div>
                <div style={{ fontSize: '0.8rem', color: '#a1a1aa', fontWeight: 600 }}>Studio Pack</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ffffff', margin: '4px 0' }}>
                  75 <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#d8b4fe' }}>Tokens</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginBottom: '12px' }}>
                  $0.133 / token • +50% bonus
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleBuyPack('studio')}
                disabled={Boolean(isProcessing)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '8px',
                  background: 'rgba(168, 85, 247, 0.25)',
                  border: '1px solid rgba(168, 85, 247, 0.45)',
                  color: '#ffffff',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {isProcessing === 'studio' ? <Loader2 size={14} className="spin" /> : '$9.99 USD'}
              </button>
            </div>

            {/* Agency Pack */}
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <div>
                <div style={{ fontSize: '0.8rem', color: '#a1a1aa', fontWeight: 600 }}>Agency Mega Pack</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#ffffff', margin: '4px 0' }}>
                  175 <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#d8b4fe' }}>Tokens</span>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginBottom: '12px' }}>
                  $0.114 / token • +75% bonus
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleBuyPack('agency')}
                disabled={Boolean(isProcessing)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '8px',
                  background: 'rgba(168, 85, 247, 0.25)',
                  border: '1px solid rgba(168, 85, 247, 0.45)',
                  color: '#ffffff',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {isProcessing === 'agency' ? <Loader2 size={14} className="spin" /> : '$19.99 USD'}
              </button>
            </div>
          </div>
        )}

        {/* Tab 2: Creator Pro Subscription */}
        {activeTab === 'pro' && (
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.12) 0%, rgba(245, 158, 11, 0.06) 100%)',
              border: '1px solid rgba(234, 179, 8, 0.45)',
              borderRadius: '14px',
              padding: '1.25rem',
              marginBottom: '1.25rem'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#fef08a', fontWeight: 800 }}>
                  Creator Pro Membership
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#d4d4d8' }}>
                  The ultimate creative suite for voice performers and video creators.
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fef08a' }}>$9.99</div>
                <div style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>per month</div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: '#e4e4e7' }}>
                <Check size={16} color="#facc15" />
                <span><strong>100 Monthly Refresh Tokens</strong> included ($20 value)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: '#e4e4e7' }}>
                <Check size={16} color="#facc15" />
                <span><strong>Unlimited Clean 1080p Master Video Exports</strong> (zero watermarks)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: '#e4e4e7' }}>
                <Check size={16} color="#facc15" />
                <span><strong>Unlimited Lossless 24-bit WAV Master Stem Downloads</strong></span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: '#e4e4e7' }}>
                <Check size={16} color="#facc15" />
                <span><strong>Cinema Match Acoustic DSP Conditioner</strong> unlocked</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: '#e4e4e7' }}>
                <Check size={16} color="#facc15" />
                <span>Golden <strong>PRO Badge</strong> on your profile and duet scenes</span>
              </div>
            </div>

            {isPro ? (
              <div style={{ display: 'flex', gap: '10px' }}>
                <div
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    background: 'rgba(34, 197, 94, 0.15)',
                    border: '1px solid rgba(34, 197, 94, 0.4)',
                    color: '#4ade80',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    textAlign: 'center'
                  }}
                >
                  ✓ You are an active Creator Pro subscriber
                </div>
                <button
                  type="button"
                  onClick={handleOpenPortal}
                  disabled={Boolean(isProcessing)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    color: '#ffffff',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {isProcessing === 'portal' ? <Loader2 size={14} className="spin" /> : <ExternalLink size={14} />}
                  Manage Subscription
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleSubscribePro}
                disabled={Boolean(isProcessing)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
                  border: 'none',
                  color: '#000000',
                  fontSize: '0.92rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 0 20px rgba(234, 179, 8, 0.35)'
                }}
              >
                {isProcessing === 'creator_pro' ? <Loader2 size={16} className="spin" /> : <Crown size={16} />}
                Upgrade to Creator Pro ($9.99 / Month)
              </button>
            )}
          </div>
        )}

        {/* Footer Guarantee & Fair Play Rules */}
        <div
          style={{
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            paddingTop: '12px',
            fontSize: '0.74rem',
            color: '#71717a',
            lineHeight: 1.5
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#a1a1aa', fontWeight: 600, marginBottom: '4px' }}>
            <ShieldCheck size={14} color="#22c55e" />
            SoulSync Fair Play & Always-Free Policy
          </div>
          Rehearsing preset scenes, recording takes, and sharing watermarked duets is <strong>100% free forever</strong>. Tokens only power high-compute AI operations (custom YouTube stem isolation) and watermark-free master exports.
        </div>
      </div>
    </div>,
    document.body
  );
};
